﻿import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FCC } from "types";

import { useCheckPartnerCustomer } from "@/apps/partner/hooks/useCheckPartnerCustomer";
import type { AddDeviceFormType } from "@/apps/partner/types";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { Notification } from "@/components/Interface/Notification/Notification";
import { BACKEND_EMAIL_REGEX } from "@/utils/constants";

import { PersonalDetailsFormFields } from "./PersonalDetailsFormFields";

/**
 * This component handles the personal details step in the onboarding process.
 * It manages form state for both private and business customers, handles
 * form submission for new and existing users, and navigates to the next step
 * upon successful submission.
 *
 * Key features:
 * - Checks if customer already exists by email (CSC check)
 * - Hides personal details form when customer exists
 * - Switches between private and business customer forms
 * - Handles form state and validation
 */
export const PersonalDetails: FCC = () => {
  const { t } = useTranslation();
  const {
    register,
    watch,
    setValue,
    unregister,
    formState: { errors },
  } = useFormContext<AddDeviceFormType>();

  const emailValue = watch("mail") || "";
  const { isExistingCustomer, isChecking } =
    useCheckPartnerCustomer(emailValue);

  // Sync the isExistingCustomer flag into the form data
  // and unregister customer fields when customer exists to skip validation
  useEffect(() => {
    setValue("isExistingCustomer", isExistingCustomer);

    if (isExistingCustomer) {
      unregister("customer");
    }
  }, [isExistingCustomer, setValue, unregister]);

  return (
    <div id="partner-personal-details" className="flex w-full flex-col gap-6">
      <Notification
        title={t("partnerPortal:addDevice.step4.hintTitle")}
        description={t("partnerPortal:addDevice.step4.hint")}
      />

      <div className="flex w-full flex-col tablet:w-1/2">
        <FormField
          id="new-email-input"
          className="tablet:pr-2.5"
          label={t("Common.generalForm.fields.email.label")}
          autocomplete="email"
          name="mail"
          errors={errors}
          register={register}
          type="email"
          placeholder={t("translation:Common.generalForm.fields.email.label")}
          options={{
            required: t("translation:label.validation.required"),
            pattern: {
              value: BACKEND_EMAIL_REGEX,
              message: t(
                "user-account.personalDetails.loginData.currentEmailError",
              ),
            },
            maxLength: 50,
            invalidEmailError: t(
              "user-account.personalDetails.loginData.currentEmailError",
            ),
          }}
        />

        {isChecking && (
          <p className="mt-1 text-body-sm text-foreground-secondary">
            {t("partnerPortal:addDevice.step4.checkingCustomer")}
          </p>
        )}
      </div>

      {isExistingCustomer ? (
        <Notification
          description={t("partnerPortal:addDevice.step4.existingCustomer")}
        />
      ) : (
        <PersonalDetailsFormFields />
      )}
    </div>
  );
};
